import { useState } from 'react';

import { Card, Empty, StatTile } from './ui';
import { TimeSeries } from './TimeSeries';
import { ago } from '../lib/format';
import { useApi } from '../lib/useApi';

interface Spend {
  windowDays: number;
  daily: Array<{ date: string; cost: number; currency: string }>;
  totalCost: number;
  currency: string;
  bySubject: Array<{
    subject: string; label: string; provider: string; cost: number; currency: string;
    inputTokens: string; outputTokens: string; cacheReadTokens: string; models: string[];
  }>;
  byModel: Array<{ model: string; provider: string; region: string; cost: number; inputTokens: string; outputTokens: string; requests: number }>;
  accounts: Array<{
    displayName: string; provider: string; region: string | null;
    lastPulledAt: string | null; lastError: string | null;
  }>;
}

const money = (n: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);

/** Token counts arrive as strings (BigInt over JSON) and are large. */
const tokens = (v: string | number) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
  return String(n);
};

export function LlmSpend() {
  const [days, setDays] = useState(30);
  const { data, error, loading } = useApi<Spend>(`/llm/usage?days=${days}`);

  if (loading && !data) return <Card title="Spend"><p className="empty">Loading spend…</p></Card>;
  if (error && !data) return <Card title="Spend"><p className="form-error">{error}</p></Card>;
  if (!data) return null;

  const collected = data.accounts.filter((a) => a.lastPulledAt);
  const failing = data.accounts.filter((a) => a.lastError);
  const pending = data.accounts.filter((a) => !a.lastPulledAt && !a.lastError);

  // The chart needs one point per day. Gaps are real — collection began when
  // PlatformIQ did — so days are plotted as reported rather than back-filled
  // with zeroes, which would draw a confident line through days nobody measured.
  const points = data.daily.map((d) => ({ t: new Date(d.date), v: d.cost }));
  const peak = data.daily.reduce((m, d) => Math.max(m, d.cost), 0);
  const perDay = data.daily.length ? data.totalCost / data.daily.length : 0;

  return (
    <>
      <div className="page-head">
        <h2>Spend</h2>
        <div className="filters">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" className={d === days ? 'tab tab-on' : 'tab'}
              onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {pending.length > 0 && (
        <div className="notice">
          Not yet collected: {pending.map((a) => a.displayName).join(', ')}. Bedrock and Vertex
          collection is not built yet — their credentials are stored and waiting.
        </div>
      )}
      {failing.map((a) => (
        <div key={a.displayName} className="notice notice-bad">
          <strong>{a.displayName}</strong> failed to collect: {a.lastError}
        </div>
      ))}

      <div className="tiles">
        <StatTile label={`Total, last ${data.windowDays} days`} value={money(data.totalCost, data.currency)} />
        <StatTile label="Average per day" value={money(perDay, data.currency)} />
        <StatTile label="Busiest day" value={money(peak, data.currency)} />
        <StatTile label="Providers reporting" value={`${collected.length} of ${data.accounts.length}`} />
      </div>

      {data.daily.length > 0 ? (
        <Card title="Daily spend">
          <TimeSeries
            title="Daily spend"
            series={[{ label: `${data.currency} per day`, color: 'var(--series-1)', points }]}
            format={(v) => money(v, data.currency)}
          />
          <p className="muted small">
            As reported by the provider's own cost report — this is the billed figure, not an estimate.
          </p>
        </Card>
      ) : (
        <Card title="Daily spend"><Empty>No spend recorded in this window.</Empty></Card>
      )}

      <Card title="By API key">
        {/* The honest caveat, stated where the numbers are rather than in a footnote.
            Anthropic bills per model and token type but never per API key, so this
            column is derived — accurately, but derived. */}
        <p className="muted small">
          Anthropic reports tokens per API key but bills only per model, so these amounts are
          <strong> apportioned</strong>: each key's share of every priced token type. They sum
          exactly to the billed total above.
        </p>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">API key</th>
                <th scope="col">Models</th>
                <th scope="col" className="num">Input</th>
                <th scope="col" className="num">Output</th>
                <th scope="col" className="num">Cache read</th>
                <th scope="col" className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.bySubject.map((s) => (
                <tr key={s.subject}>
                  <td><code className="stack-name">{s.label}</code></td>
                  <td className="small">{s.models.join(', ')}</td>
                  <td className="num">{tokens(s.inputTokens)}</td>
                  <td className="num">{tokens(s.outputTokens)}</td>
                  <td className="num">{tokens(s.cacheReadTokens)}</td>
                  <td className="num">{money(s.cost, s.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.bySubject.length === 0 && <Empty>Nothing attributed yet.</Empty>}
      </Card>

      <Card title="By model">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col">Where</th>
                <th scope="col" className="num">Input</th>
                <th scope="col" className="num">Output</th>
                <th scope="col" className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.byModel.map((m) => (
                <tr key={`${m.provider}|${m.region}|${m.model}`}>
                  <td className="mono small">{m.model}</td>
                  <td className="small">{m.provider}{m.region ? ` · ${m.region}` : ""}</td>
                  <td className="num">{tokens(m.inputTokens)}</td>
                  <td className="num">{tokens(m.outputTokens)}</td>
                  <td className="num">{money(m.cost, data.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.byModel.length === 0 && <Empty>No model usage in this window.</Empty>}
      </Card>

      <Card title="Provider accounts">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>Account</th><th>Provider</th><th>Region</th><th>Last pulled</th><th>Status</th></tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr key={a.displayName}>
                  <td>{a.displayName}</td>
                  <td>{a.provider}</td>
                  <td className="mono small">{a.region || <span className="muted">—</span>}</td>
                  <td>{a.lastPulledAt ? ago(a.lastPulledAt) : <span className="muted">never</span>}</td>
                  <td>
                    {a.lastError
                      ? <span className="form-error small">{a.lastError}</span>
                      : a.lastPulledAt
                        ? <span className="pill pill-up"><span aria-hidden="true">●</span> ok</span>
                        : <span className="muted small">collector not built yet</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
